import { Button, Card, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconPhotoCheck } from '@tabler/icons-react';
import { BRAND_KIT_COPY as C, type FactoryAssetPlatform, type TvPlatformKey } from '@/lib/constants/appFactory';
import { BrandKitUtils, type BrandKitScopeData } from '../BrandKitUtils';
import { useIsFactoryOperator } from '../useAppFactory';
import { useAssetSetDecisions, type AssetSetDraft } from '../useAssetSetDecisions';
import { AssetSetDraftCard } from './AssetSetDraftCard';

interface BrandKitDraftsProps {
  appCfgId: number;
  /** Every platform's pending draft that came from the Brand Kit. */
  drafts: AssetSetDraft[];
  /** Each platform's sets (a draft made before its live images says so). */
  data: Partial<Record<FactoryAssetPlatform, BrandKitScopeData>>;
  tv: readonly TvPlatformKey[];
  disabled?: boolean;
}

/**
 * The Brand Kit's drafts, reviewed together (3.42.0): one card per platform
 * with its TV screens at true size and its quality gate, and ONE action for
 * all of them — a second draft left behind is how older art used to come back
 * by mistake. A blocked draft stays a draft; the ones that pass are published.
 */
export function BrandKitDrafts({ appCfgId, drafts, data, tv, disabled }: BrandKitDraftsProps) {
  const decisions = useAssetSetDecisions(appCfgId, tv);
  const isOperator = useIsFactoryOperator();
  if (drafts.length === 0) return null;
  const anyPasses = drafts.some((d) => BrandKitUtils.gatePassed(d.set));

  return (
    <Card withBorder radius="md" p="md" data-testid="brand-kit-drafts">
      <Stack gap="md">
        <Group gap="xs">
          <IconPhotoCheck size={20} />
          <Text fw={600} size="md">
            {C.draftsTitle}
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          {C.draftsIntro}
        </Text>

        {drafts.length > 1 && (
          <Group gap="sm">
            <Tooltip label={C.blockedTooltip} disabled={anyPasses} withArrow>
              <Button onClick={() => decisions.approveAll(drafts)} loading={decisions.busy} disabled={disabled || !anyPasses}>
                {C.approveAll}
              </Button>
            </Tooltip>
            <Button variant="default" onClick={() => decisions.discardAll(drafts)} loading={decisions.busy} disabled={disabled}>
              {C.discardAll}
            </Button>
          </Group>
        )}

        {drafts.map((draft) => (
          <AssetSetDraftCard
            key={draft.set.assetSetId}
            set={draft.set}
            scope={draft.scope}
            tv={tv}
            assetSets={data[draft.scope]?.assetSets ?? []}
            disabled={disabled}
            busy={decisions.busy}
            isOperator={isOperator}
            onApprove={() => decisions.approve(draft)}
            onDiscard={() => decisions.discard(draft)}
            onOverride={(_, reason) => decisions.override(draft, reason)}
          />
        ))}
      </Stack>
    </Card>
  );
}
